import { globalStore } from '@thewhite/react-global-module/lib/store';
import isEqual from 'lodash.isequal';
import { Provider } from 'mobx-react';
import * as React from 'react';
import { HashRouter, Route, Switch } from 'react-router-dom';
import Preloader from '../modules/Global/components/preloader/preloader';
import NotFound from '../modules/Global/notFoundContainer';
import refStore from '../modules/Global/store/refStore';
import userStore from '../modules/Global/store/userStore';
import MainWrap from '../modules/Wrappers/mainWrap/mainWrapComponent';
import participantStore from '../store/participantStore';

interface IMainRouterProps {
    history?: History;
    location?: Location;
}

const stores = { globalStore, refStore, userStore, participantStore };

export default class MainRouter extends React.Component<IMainRouterProps> {
    shouldComponentUpdate(nextProps: Readonly<{ history?: History; location?: Location; }>): boolean {
        return !isEqual(this.props.location, nextProps.location);
    }

    render() {
        const LoginPage          = React.lazy(() => import('../modules/Auth/pages/login/login'));
        const MainPage           = React.lazy(() => import('../modules/Main/pages/mainPage/mainPage'));
        const UserList           = React.lazy(() => import('../modules/User/pages/userList/userListContainer'));
        const UserCard           = React.lazy(() => import('../modules/User/pages/userCard/userCardContainer'));
        const CalendarView       = React.lazy(() => import('../modules/Booking/pages/calendarView/calendarView'));
        const ParticipantList    = React.lazy(() => import('../modules/Participant/pages/list/participantList'));
        const ParticipantView    = React.lazy(() => import('../modules/Participant/pages/view/participantView'));
        const EmployeeList       = React.lazy(() => import('../modules/Employee/pages/list/employeeListView'));
        const EmployeeCard       = React.lazy(() => import('../modules/Employee/pages/card/employeeCardView'));
        const ArticleList        = React.lazy(() => import('../modules/Article/pages/list/articleListView'));
        const ArticleCard        = React.lazy(() => import('../modules/Article/pages/card/articleCardView'));
        const Banners            = React.lazy(() => import('../modules/Banners/pages/settings/bannersSettings'));
        const ItemList           = React.lazy(() => import('../modules/Item/pages/list/itemListView'));
        const ItemCard           = React.lazy(() => import('../modules/Item/pages/card/itemCardView'));
        const ItemsCalculator    = React.lazy(() => import('../modules/Item/pages/calculator/itemsCalculator'));
        const ChatList           = React.lazy(() => import('../modules/Chat/pages/list/chatListView'));
        const ChatPage           = React.lazy(() => import('../modules/Chat/pages/chat/chatContainer'));
        const VacancyList        = React.lazy(() => import('../modules/Vacancy/pages/list/vacancyListView'));
        const VacancyCard        = React.lazy(() => import('../modules/Vacancy/pages/card/vacancyCardView'));
        const ResponseList       = React.lazy(() => import('../modules/Response/pages/list/responseListView'));
        const ResponseCard       = React.lazy(() => import('../modules/Response/pages/card/responseCardView'));
        const ItemSubjectList    = React.lazy(() => import('../modules/ItemSubject/pages/list/itemSubjectListView'));
        const ItemSubjectCard    = React.lazy(() => import('../modules/ItemSubject/pages/card/itemSubjectCardView'));
        const ArticleSubjectList = React.lazy(() => import('../modules/ArticleSubject/pages/list/articleSubjectListView'));
        const ArticleSubjectCard = React.lazy(() => import('../modules/ArticleSubject/pages/card/articleSubjectCardView'));
        const SubjectGroupList   = React.lazy(() => import('../modules/SubjectGroup/pages/list/subjectGroupListView'));
        const SubjectGroupCard   = React.lazy(() => import('../modules/SubjectGroup/pages/card/subjectGroupCardView'));
        const CommentList        = React.lazy(() => import('../modules/Comment/pages/list/commentListView'));
        const CommentCard        = React.lazy(() => import('../modules/Comment/pages/card/commentCardView'));
        const AuditPage          = React.lazy(() => import('../modules/Audit/pages/audit/auditContainer'));
        const SystemSettings     = React.lazy(() => import('../modules/SystemSettings/pages/systemSettingsContainer'));
        const SyncLogs           = React.lazy(() => import('../modules/SyncLogs/pages/syncLogs/syncLogsContainer'));
        const DocumentList       = React.lazy(() => import('../modules/Document/pages/list/documentListView'));
        const DocumentCard       = React.lazy(() => import('../modules/Document/pages/card/documentCardView'));
        const OrganizationList   = React.lazy(() => import('../modules/Organization/pages/list/organizationListView'));
        const OrganizationCard   = React.lazy(() => import('../modules/Organization/pages/card/organizationCardView'));
        const ProfileSettings    = React.lazy(() => import('../modules/ProfileSettings/pages/profileSettingsContainer'));
        const SpecialitiesList   = React.lazy(() => import('../modules/Specialties/pages/list/specialityListView'));
        const SpecialityCard     = React.lazy(() => import('../modules/Specialties/pages/card/specialityCardView'));
        const PostList           = React.lazy(() => import('../modules/Posts/pages/list/postListView'));
        const PostCard           = React.lazy(() => import('../modules/Posts/pages/card/postCardView'));
        const TagList            = React.lazy(() => import('../modules/Tags/pages/list/tagListView'));
        const TagCard            = React.lazy(() => import('../modules/Tags/pages/card/tagCardView'));
        const TagCardPriority    = React.lazy(() => import('../modules/Tags/pages/cardOfPriority/tagCardPriorityView'));

        const ConsentAdvertisingReport = React.lazy(() => import('../modules/Analytics/pages/consentAdvertisingReport/consentAdvertisingReport'));

        return (
            <Provider
                {...stores}
            >
                <HashRouter>
                    <React.Suspense
                        fallback={
                            <Preloader />
                        }
                    >
                        <Switch>
                            <Route
                                exact
                                path={'/login'}
                                component={LoginPage}
                            />
                            <MainWrap
                                location={this.props.location}
                            >
                                <React.Suspense
                                    fallback={
                                        <Preloader />
                                    }
                                >
                                    <Switch>
                                        <Route
                                            exact
                                            path={'/'}
                                            component={MainPage}
                                        />
                                        <Route
                                            exact
                                            path={'/user/list'}
                                            component={UserList}
                                        />
                                        <Route
                                            exact
                                            path={'/user/:marker'}
                                            component={UserCard}
                                        />
                                        <Route
                                            exact
                                            path={'/calendar'}
                                            component={CalendarView}
                                        />
                                        <Route
                                            exact
                                            path={'/participant/list'}
                                            component={ParticipantList}
                                        />
                                        <Route
                                            exact
                                            path={'/participant/:participantId/view'}
                                            component={ParticipantView}
                                        />
                                        <Route
                                            exact
                                            path={'/employee/list'}
                                            component={EmployeeList}
                                        />
                                        <Route
                                            exact
                                            path={'/employee/:employeeId/edit'}
                                            component={EmployeeCard}
                                        />
                                        <Route
                                            exact
                                            path={'/article/list'}
                                            component={ArticleList}
                                        />
                                        <Route
                                            exact
                                            path={'/article/create'}
                                            component={ArticleCard}
                                        />
                                        <Route
                                            exact
                                            path={'/article/:articleId/edit'}
                                            component={ArticleCard}
                                        />
                                        <Route
                                            exact
                                            path={'/banners'}
                                            component={Banners}
                                        />
                                        <Route
                                            exact
                                            path={'/item/list'}
                                            component={ItemList}
                                        />
                                        <Route
                                            exact
                                            path={'/item/:itemId/edit'}
                                            component={ItemCard}
                                        />
                                        <Route
                                            exact
                                            path={'/items-calculator'}
                                            component={ItemsCalculator}
                                        />
                                        <Route
                                            exact
                                            path={'/chat/list'}
                                            component={ChatList}
                                        />
                                        <Route
                                            exact
                                            path={'/chat/:chatId'}
                                            component={ChatPage}
                                        />
                                        <Route
                                            exact
                                            path={'/vacancy/list'}
                                            component={VacancyList}
                                        />
                                        <Route
                                            exact
                                            path={'/vacancy/create'}
                                            component={VacancyCard}
                                        />
                                        <Route
                                            exact
                                            path={'/vacancy/:vacancyId/edit'}
                                            component={VacancyCard}
                                        />
                                        <Route
                                            exact
                                            path={'/response/list'}
                                            component={ResponseList}
                                        />
                                        <Route
                                            exact
                                            path={'/response/:responseId/edit'}
                                            component={ResponseCard}
                                        />
                                        <Route
                                            exact
                                            path={'/item-subject/list'}
                                            component={ItemSubjectList}
                                        />
                                        <Route
                                            exact
                                            path={'/item-subject/create'}
                                            component={ItemSubjectCard}
                                        />
                                        <Route
                                            exact
                                            path={'/item-subject/:itemSubjectId/edit'}
                                            component={ItemSubjectCard}
                                        />
                                        <Route
                                            exact
                                            path={'/article-subject/list'}
                                            component={ArticleSubjectList}
                                        />
                                        <Route
                                            exact
                                            path={'/article-subject/create'}
                                            component={ArticleSubjectCard}
                                        />
                                        <Route
                                            exact
                                            path={'/article-subject/:articleSubjectId/edit'}
                                            component={ArticleSubjectCard}
                                        />
                                        <Route
                                            exact
                                            path={'/subject-group/list'}
                                            component={SubjectGroupList}
                                        />
                                        <Route
                                            exact
                                            path={'/subject-group/create'}
                                            component={SubjectGroupCard}
                                        />
                                        <Route
                                            exact
                                            path={'/subject-group/:subjectGroupId/edit'}
                                            component={SubjectGroupCard}
                                        />
                                        <Route
                                            exact
                                            path={'/comment/list'}
                                            component={CommentList}
                                        />
                                        <Route
                                            exact
                                            path={'/comment/create'}
                                            component={CommentCard}
                                        />
                                        <Route
                                            exact
                                            path={'/comment/:commentId/edit'}
                                            component={CommentCard}
                                        />
                                        <Route
                                            exact
                                            path={'/audit'}
                                            component={AuditPage}
                                        />
                                        <Route
                                            exact
                                            path={'/system-settings'}
                                            component={SystemSettings}
                                        />
                                        <Route
                                            exact
                                            path={'/sync-logs/:marker'}
                                            component={SyncLogs}
                                        />
                                        <Route
                                            exact
                                            path={'/document/list'}
                                            component={DocumentList}
                                        />
                                        <Route
                                            exact
                                            path={'/document/create'}
                                            component={DocumentCard}
                                        />
                                        <Route
                                            exact
                                            path={'/document/:documentId/edit'}
                                            component={DocumentCard}
                                        />
                                        <Route
                                            exact
                                            path={'/organization/list'}
                                            component={OrganizationList}
                                        />
                                        <Route
                                            exact
                                            path={'/organization/:organizationId/edit'}
                                            component={OrganizationCard}
                                        />
                                        <Route
                                            exact
                                            path={'/profile-settings'}
                                            component={ProfileSettings}
                                        />
                                        <Route
                                            exact
                                            path={'/specialities/list'}
                                            component={SpecialitiesList}
                                        />
                                        <Route
                                            exact
                                            path={'/specialities/create'}
                                            component={SpecialityCard}
                                        />
                                        <Route
                                            exact
                                            path={'/specialities/:specialityId/edit'}
                                            component={SpecialityCard}
                                        />
                                        <Route
                                            exact
                                            path={'/post/list'}
                                            component={PostList}
                                        />
                                        <Route
                                            exact
                                            path={'/post/create'}
                                            component={PostCard}
                                        />
                                        <Route
                                            exact
                                            path={'/post/:postId/edit'}
                                            component={PostCard}
                                        />
                                        <Route
                                            exact
                                            path={'/tag/list'}
                                            component={TagList}
                                        />
                                        <Route
                                            exact
                                            path={'/tag/create'}
                                            component={TagCard}
                                        />
                                        <Route
                                            exact
                                            path={'/tag/:tagId/edit'}
                                            component={TagCard}
                                        />
                                        <Route
                                            exact
                                            path={'/tag/:tagId/edit-priority'}
                                            component={TagCardPriority}
                                        />
                                        <Route
                                            exact
                                            path={'/analytics/consent-advertising-report'}
                                            component={ConsentAdvertisingReport}
                                        />
                                        <Route
                                            component={NotFound}
                                        />
                                    </Switch>
                                </React.Suspense>
                            </MainWrap>
                        </Switch>
                    </React.Suspense>
                </HashRouter>
            </Provider>
        );
    }
}
